import { trim } from 'lodash';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';

import { oryWhoAmI } from '@revelio/auth';
import { validateEmail } from '@revelio/iso-utility';

import { environment } from '../../environment';
import { SurfContext } from '../../surf-context';
import { createVerifiedUserA } from '../utils/create-verified-user-a';
import { isPressSignup } from '../utils/is-press-signup';
import {
  SalesCta,
  signupEmailFieldError,
  signupErrorMessage,
} from '../utils/signup-error-message';
import type { FormValues } from './models';
import {
  LegalFooter,
  SECTION_SPACING,
  ThemeHeading,
  ThemeLink,
  ThemeTagline,
} from './theme/theme';
import { Button, Field, Spinner, Stack, TextInput, uiStyles } from './ui';

const Step0A = ({
  setFormData,
  setActiveStep,
}: {
  setFormData: Dispatch<SetStateAction<FormValues>>;
  setActiveStep: (step: number) => void;
}) => {
  // Carried onto the login link so `?s=website` and `?theme` survive.
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const redirect_uri = searchParams.get('redirect_uri');

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null);

  // No password on this arm — it creates a CRM lead, not a Kratos identity.
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>();

  const csrfToken = useContext(SurfContext);

  const checkStatus = useCallback(async () => {
    const user = await oryWhoAmI();
    if (user.active) {
      window.location.replace(redirect_uri || environment.PUBLIC_REACT_APP_URL);
    }
  }, [redirect_uri]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleSignUp = handleSubmit(async ({ name, email }: FormValues) => {
    setLoading(true);
    setErrorMessage(null);
    clearErrors('email');

    // Organization is collected at step 2 now, so it isn't part of the
    // create-lead body — this arm sends it later via /interested-trial.
    const createBody = {
      name: trim(name),
      email: trim(email),
      ...(isPressSignup(searchParams) ? { isPress: true } : {}),
    };

    try {
      await createVerifiedUserA({ body: createBody, csrfToken });
      setFormData({ ...createBody });
      // Skips the verification step: this arm never sends a code.
      setActiveStep(2);
    } catch (err) {
      console.error(err);
      const emailError = signupEmailFieldError(err);
      if (emailError) {
        setError('email', { type: 'server', message: emailError });
      }
      setErrorMessage(signupErrorMessage(err));
    }

    setLoading(false);
  });

  return (
    <Stack gap={SECTION_SPACING} fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Create account</ThemeHeading>
        <ThemeTagline>
          Already have an account?{' '}
          <ThemeLink href={`/login${search}`}>Log in</ThemeLink>
        </ThemeTagline>
      </Stack>

      <form onSubmit={handleSignUp}>
        <Stack gap="16px">
          <Field error={errors.name?.message}>
            <TextInput
              invalid={!!errors?.name}
              placeholder="First and last name"
              aria-label="First and last name"
              autoComplete="name"
              {...register('name', {
                required: 'Name is required',
              })}
            />
          </Field>
          <Field
            error={
              // One span: Field's error box is flex, which would lay the
              // message and the CTA out as separate items and eat the
              // whitespace between them.
              errors.email && (
                <span>
                  {errors.email.message}
                  {errors.email.type === 'server' && <SalesCta />}
                </span>
              )
            }
          >
            <TextInput
              invalid={!!errors?.email}
              placeholder="Work Email"
              aria-label="Work Email"
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                validate: {
                  emailSyntax: (email: string) =>
                    validateEmail(email) || 'Valid email is required',
                },
              })}
            />
          </Field>
        </Stack>
        <Button style={{ marginTop: '24px' }} disabled={loading} type="submit">
          {loading ? (
            <span className={uiStyles.buttonLoading}>
              Creating account...
              <Spinner size="xs" />
            </span>
          ) : (
            'Create account'
          )}
        </Button>
        {errorMessage && <p className={uiStyles.formError}>{errorMessage}</p>}
      </form>

      <LegalFooter action="Create account" />
    </Stack>
  );
};

export default Step0A;
